// TileOwnerModal.js
import React, { useEffect, useRef, useContext } from 'react';
import LogInRequiredModal from '../LogInRequiredModal';
import { BackendContext } from '../../Context/BackendContext';
import { useNavigate } from 'react-router-dom';
import { Heart, X, MapPin, Share, Twitter, Instagram, Youtube, Twitch } from 'lucide-react';
import { 
  Divider, 
  Spinner,
  Button,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Tooltip,
} from "@heroui/react";

// ---- Chunk + RLE helpers ----
const CHUNK = 64; // must match your backend
const CANVAS_SIZE = 512;

const base64ToBytes = (b64) => {
  if (typeof Buffer !== 'undefined') return Uint8Array.from(Buffer.from(b64, 'base64'));
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i) & 0xff;
  return out;
};
const base64ToU32 = (b64) => {
  const bytes = base64ToBytes(b64);
  const buf = new ArrayBuffer(bytes.byteLength);
  new Uint8Array(buf).set(bytes);
  return new Uint32Array(buf);
};
const rleDecode = (u32, total) => {
  const bits = new Uint8Array(total);
  let curr = 0, idx = 0;
  for (let k = 0; k < u32.length; k++) {
    const n = u32[k] >>> 0;
    if (curr === 1) bits.fill(1, idx, idx + n);
    idx += n;
    curr ^= 1;
  }
  return bits;
};
const chunkOrigin = (cx, cy) => ({ col1: cx * CHUNK + 1, row1: cy * CHUNK + 1 }); // 1-based

// Draw decoded chunks (white squares)
function drawDecodedMask(ctx, decodedChunks, minCol, minRow, dx, dy, scale) {
  ctx.imageSmoothingEnabled = false;
  ctx.fillStyle = 'white';
  for (const ch of decodedChunks) {
    const { origin, width, height, bits } = ch;
    const baseX = origin.col1 - 1; // 0-based world
    const baseY = origin.row1 - 1;
    for (let j = 0; j < height; j++) {
      const rowOff = j * width;
      for (let i = 0; i < width; i++) {
        if (!bits[rowOff + i]) continue;
        const worldX = baseX + i;
        const worldY = baseY + j;
        const localX = worldX - (minCol - 1);
        const localY = worldY - (minRow - 1);
        ctx.fillRect(dx + localX * scale, dy + localY * scale, scale, scale);
      }
    }
  }
}

const ArtworkGuestModal = ({
  artworkId,
  onCloseModal,
}) => {
  const navigate = useNavigate();
  const backendContext = useContext(BackendContext);
  
  const modalRef = useRef(null);
  const canvasRef = useRef(null);

  const [isCallingBackend, setIsCallingBackend] = React.useState(false);
  const [artworkLoading, setArtworkLoading] = React.useState(true); // true until tile fetch finishes
  const [artwork, setArtwork] = React.useState(null);

  const [hasUserLiked, setHasUserLiked] = React.useState(null);
  const [reportModalOpen, setReportModalOpen] = React.useState(false);

  const [timeRemaining, setTimeRemaining] = React.useState('00:00:00');
  
  const [showScrollbar, setShowScrollbar] = React.useState(false);
  const scrollTimeout = useRef(null);
  
  const [loginModalOpen, setLoginModalOpen] = React.useState(false);
  const [isSharing, setIsSharing] = React.useState(false);
  const [copied, setCopied] = React.useState(false);

  // cache of decoded chunks (for fast redraw) + bbox (1-based columns/rows)
  const decodedRef = React.useRef([]); 
  const bboxCacheRef = React.useRef(null);

  // Compute bbox from decodedRef (1-based col/row space)
  function computeBBoxFromDecoded(decoded) {
    let minCol =  Infinity, minRow =  Infinity;
    let maxCol = -Infinity, maxRow = -Infinity;

    for (const ch of decoded) {
      const { origin, width, height, bits } = ch;
      const baseX = origin.col1 - 1; // world 0-based
      const baseY = origin.row1 - 1;
      for (let j = 0; j < height; j++) {
        const rowOff = j * width;
        for (let i = 0; i < width; i++) {
          if (!bits[rowOff + i]) continue;
          const x = baseX + i, y = baseY + j;
          if (x + 1 < minCol) minCol = x + 1;
          if (y + 1 < minRow) minRow = y + 1;
          if (x + 1 > maxCol) maxCol = x + 1;
          if (y + 1 > maxRow) maxRow = y + 1;
        }
      }
    }

    if (!Number.isFinite(minCol)) return { minCol:1, minRow:1, maxCol:1, maxRow:1, bboxW:1, bboxH:1 };
    const bboxW = Math.max(1, maxCol - minCol + 1);
    const bboxH = Math.max(1, maxRow - minRow + 1);
    return { minCol, minRow, maxCol, maxRow, bboxW, bboxH };
  }

  // Re-render preview using cached decodedRef + bboxCacheRef
  async function renderPreviewFromCache(art) {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
    ctx.imageSmoothingEnabled = false;

    const decoded = decodedRef.current || [];
    let bbox = bboxCacheRef.current;
    if (!bbox) bbox = computeBBoxFromDecoded(decoded); // fallback if not set yet

    const { minCol, minRow, bboxW, bboxH } = bbox;

    const margin = 8;
    const usableW = CANVAS_SIZE - margin * 2;
    const usableH = CANVAS_SIZE - margin * 2;
    const scale = Math.max(1, Math.floor(Math.min(usableW / bboxW, usableH / bboxH)));
    const drawW = bboxW * scale;
    const drawH = bboxH * scale;
    const dx = Math.floor((CANVAS_SIZE - drawW) / 2);
    const dy = Math.floor((CANVAS_SIZE - drawH) / 2);

    // 1) mask
    if (decoded.length > 0) {
      drawDecodedMask(ctx, decoded, minCol, minRow, dx, dy, scale);
    }

    // 2) image clipped inside the mask
    if (art?.imageData) {
      await new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          const originCol = art.imageOriginColumn ?? minCol; // 1-based
          const originRow = art.imageOriginRow   ?? minRow;
          const imgDw = (art.imageWidth  ?? img.width)  * scale;
          const imgDh = (art.imageHeight ?? img.height) * scale;
          const imgDx = dx + (originCol - minCol) * scale;
          const imgDy = dy + (originRow - minRow) * scale;

          ctx.save();
          if (decoded.length > 0) ctx.globalCompositeOperation = 'source-atop';
          ctx.drawImage(img, 0, 0, img.width, img.height, imgDx, imgDy, imgDw, imgDh);
          ctx.restore();
          resolve();
        };
        img.onerror = () => resolve();
        img.src = `data:image/png;base64,${art.imageData}`;
      });
    }
  }

  // --- Share helpers ---
  const getArtworkShareUrl = React.useCallback(() => {
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const pg = artwork?.page ?? 'A';
    return `${origin}/Canvas/${encodeURIComponent(pg)}/${encodeURIComponent(artworkId)}`;
  }, [artwork?.page, artworkId]);

  const copyToClipboard = async (text) => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }
      // Fallback (rare): temporarily select/execCommand
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'absolute';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  };

  const handleShare = async () => {
    if (!artworkId) return;
    const url = getArtworkShareUrl();
    const title = artwork?.title || 'ArtParcel Artwork';
    const text = `Check out this artwork on ArtParcel${artwork?.owner ? ` by @${artwork.owner}` : ''}!`;

    setIsSharing(true);
    try {
      if (navigator?.share && false) {
        // Native share (best UX on mobile)
        await navigator.share({ title, text, url });
      } else {
        // Clipboard fallback
        const ok = await copyToClipboard(url);
        if (ok) {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        } else {
          // As a last resort, open a Twitter intent prefilled
          const tw = `https://twitter.com/intent/tweet?text=${encodeURIComponent(`${text} ${url}`)}`;
          window.open(tw, '_blank', 'noopener,noreferrer');
        }
      }
    } catch (e) {
      // user canceled share or error — no-op
      // console.error('share failed', e);
    } finally {
      setIsSharing(false);
    }
  };

  // --- Fetch artwork and render only its art ---
  useEffect(() => {
    const fetchAndRenderArtwork = async () => {
      if (!artworkId) return;
      setArtworkLoading(true);

      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');

      canvas.width = CANVAS_SIZE;
      canvas.height = CANVAS_SIZE;

      // Clear to transparent background
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      try {
        // 1) Fetch artwork (no pixels) + its mask chunks in parallel
        const [art, chunks] = await Promise.all([
          backendContext.fetchArtworkWithSocials(artworkId),
          backendContext.fetchArtworkSilhouetteChunks(artworkId),
        ]);
        setArtwork(art || null);

        const hasChunks = Array.isArray(chunks) && chunks.length > 0;

        // 2) Decode chunks + compute bbox
        let decoded = [];
        let minCol =  Infinity, minRow =  Infinity;
        let maxCol = -Infinity, maxRow = -Infinity;

        if (hasChunks) {
          for (const c of chunks) {
            const width = c.width;
            const height = c.height;
            const rleU32 = base64ToU32(c.rle);
            const bits = rleDecode(rleU32, width * height);
            const origin = chunkOrigin(c.chunkX ?? c.cx, c.chunkY ?? c.cy);
            decoded.push({ width, height, origin, bits });

            // bbox scan
            const baseX = origin.col1 - 1; // 0-based world coords
            const baseY = origin.row1 - 1;
            for (let j = 0; j < height; j++) {
              const rowOff = j * width;
              for (let i = 0; i < width; i++) {
                if (!bits[rowOff + i]) continue;
                const x = baseX + i, y = baseY + j;
                if (x + 1 < minCol) minCol = x + 1; // store as 1-based
                if (y + 1 < minRow) minRow = y + 1;
                if (x + 1 > maxCol) maxCol = x + 1;
                if (y + 1 > maxRow) maxRow = y + 1;
              }
            }
          }
        }

        // 3) Prefer server-stored bbox if present
        if (art?.maskBBoxMinColumn && art?.maskBBoxMinRow) {
          minCol = art.maskBBoxMinColumn;
          minRow = art.maskBBoxMinRow;
          maxCol = art.maskBBoxMaxColumn ?? maxCol;
          maxRow = art.maskBBoxMaxRow ?? maxRow;
        }

        // If there’s no mask at all, use the image size to fit preview nicely
        if (!Number.isFinite(minCol)) {
          if (art?.imageWidth && art?.imageHeight) {
            minCol = 1; minRow = 1;
            maxCol = art.imageWidth;
            maxRow = art.imageHeight;
          } else {
            // last-resort benign bbox
            minCol = 1; minRow = 1; maxCol = 1; maxRow = 1;
          }
        }

        const bbox = {
          minCol, minRow, maxCol, maxRow,
          bboxW: Math.max(1, maxCol - minCol + 1),
          bboxH: Math.max(1, maxRow - minRow + 1),
        };

        // 4) Cache for instant redraws later, then render once now
        decodedRef.current   = decoded;
        bboxCacheRef.current = bbox;

        await renderPreviewFromCache(art);

        if(backendContext.userProfile) {
          setHasUserLiked(await backendContext.checkIfUserHasLikedArtwork(artworkId));
        }

        setArtworkLoading(false);
      } catch (err) {
        console.error('fetchAndRenderArtwork (chunks) failed:', err);
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        setArtworkLoading(false);
      }
    };

    fetchAndRenderArtwork();
  }, [artworkId, backendContext]);

  // --- Countdown timer for ownership expiration ---
  useEffect(() => {
    const updateCountdown = () => {
      if (!artwork || !artwork.expiresAt) return;

      const expiresAt = new Date(artwork.expiresAt);
      const now = new Date();
      const diff = Math.max(0, expiresAt.getTime() - now.getTime());

      const hours = Math.floor(diff / 1000 / 3600);
      const minutes = Math.floor((diff / 1000 / 60) % 60);
      const seconds = Math.floor((diff / 1000) % 60);

      setTimeRemaining(
        `${String(hours).padStart(2,'0')}:${String(minutes).padStart(2,'0')}:${String(seconds).padStart(2,'0')}`
      );
    };

    if (artwork?.expiresAt) {
      updateCountdown();
      const interval = setInterval(updateCountdown, 1000);
      return () => clearInterval(interval);
    }
  }, [artwork]);

  // Scrollbar hint hide
  const handleScroll = () => {
    setShowScrollbar(true);
    if (scrollTimeout.current) clearTimeout(scrollTimeout.current);
    scrollTimeout.current = setTimeout(() => setShowScrollbar(false), 1000);
  };

  const onLikeArtwork = async () => {
    if (!backendContext.userProfile) {
      setLoginModalOpen(true);
      return;
    }

    await backendContext.likeArtwork(artworkId);
    setHasUserLiked(!hasUserLiked); // Toggle like state
    setArtwork(prev => ({
      ...prev,
      likesCount: (prev.likesCount || 0) + (hasUserLiked ? -1 : 1) // Increment or decrement likes count
    }));
  }

  const onReportArtwork = async () => {
    setReportModalOpen(true);
  }

  const onCancelReport = () => {
    if (!isCallingBackend) setReportModalOpen(false);
  }

  const onConfirmReport = async () => {
    try {
      setIsCallingBackend(true);
      await backendContext.createReport(artworkId);
      setReportModalOpen(false);
    } catch (e) {
      console.error('Report failed:', e);
    } finally {
      setIsCallingBackend(false);
    }
  }

  return (
    <>
      {isCallingBackend && (
        <div className="fixed inset-0 z-[1110] bg-black bg-opacity-70 flex items-center justify-center">
          <div className="bg-white rounded-lg p-4 flex items-center gap-2">
            <Spinner color="primary" size="lg" />
            <span className="text-black">Working…</span>
          </div>
        </div>
      )}

      <div className="fixed inset-0 bg-black/60 z-[900] flex items-center justify-center">
        <div
          ref={modalRef}
          className={`pointer-events-auto bg-slate-900/80 rounded-xl p-4 relative max-h-[90vh] w-[550px]`}
        >
          <button
            onClick={onCloseModal}
            className="absolute top-4 right-4 text-zinc-400 hover:text-white"
          >
            <X className="w-6 h-6" />
          </button>

          <h2 className="mb-2 text-center font-bold">
            {artworkLoading ? 'Loading Artwork…' : `${artwork?.title ?? '—'}`}
          </h2>
          
          {/* Scrollable content container */}
          <div
            className="overflow-y-visible overflow-x-hidden max-h-[calc(85vh-100px)] w-full"
            onScroll={handleScroll}
            style={{ scrollbarWidth: showScrollbar ? 'none' : 'none' }}
          >
            {/* Artwork-only canvas */}
            <div className="relative w-full h-[512px] flex items-center justify-center">
              {artworkLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-slate-900/80 z-10 rounded-lg">
                  <Spinner color="primary" size="lg" />
                </div>
              )}
              <canvas
                ref={canvasRef}
                style={{ imageRendering: 'pixelated', border: '1px solid black', cursor: 'crosshair' }}
              />
            </div>

            {/* Likes + tag */}
            <div className="mt-2 mb-2 flex justify-between items-center gap-2 text-sm">
              {artworkLoading ? (
                <Heart className="animate-spin text-gray-400" />
              ) : (
                <>
                  <div className="flex items-center gap-2">
                    <Button isIconOnly aria-label="Like"
                      onPress={onLikeArtwork}
                      className={hasUserLiked ? "bg-pink-700" : "default"}
                    >
                      <Heart className="text-white-800"/>
                    </Button>
                    <p className="text-l font-bold text-zinc-200">{artwork?.likesCount ?? 0}</p>
                  </div>
                  <Tooltip
                    content="Copied!"
                    color="success"
                    placement="top"
                    isOpen={copied}          // shows only when copied === true
                    motionProps={{
                      variants: {
                        enter: { opacity: 1, scale: 1, transition: { duration: 0.2 } },
                        exit: { opacity: 0, scale: 0.95, transition: { duration: 0.15 } },
                      },
                    }}
                  >
                    <Button
                      isIconOnly
                      size="md"
                      aria-label="Share"
                      onPress={handleShare}
                      isLoading={isSharing}
                      className={`bg-zinc-700 text-xs px-2 py-1 ${copied ? 'bg-green-700' : ''}`}
                      title="Share Artwork"
                    >
                      <Share className="text-gray-300" />
                    </Button>
                  </Tooltip>
                </>
              )}
            </div>

            {!artworkLoading && artwork && (
              <>
                {/* Header row: Title + location */}
                <div className="flex justify-between items-center mb-2">
                  <div className="flex items-center gap-2">
                    <h2 className="text-xl font-bold">{artwork?.title ?? 'Untitled'}</h2>
                  </div>

                  <div className="flex items-center gap-2 text-zinc-400">
                    <MapPin />
                    {/* If you store page/row/column where the artwork originated or its "anchor" */}
                    <p className="text-m font-bold">
                      {artwork?.page ? `${artwork.page}` : '—'}
                    </p>
                  </div>
                </div>

                {/* Owner */}
                <div className="mb-4">
                  <div className="text-zinc-400 text-xs mb-1">OWNER</div>
                  <div className="flex justify-between items-center">
                    <div
                      className="text-blue-400 font-medium cursor-pointer hover:underline"
                      onClick={() => navigate(`/User/${artwork?.owner ?? ''}`)}
                    >
                      {"@" + (artwork?.owner ?? "N/A")}
                    </div>

                    {/* Socials (optional; requires your data shape) */}
                    {artwork?.ownerProfile?.socials && (
                      <div className="flex gap-2 text-zinc-300">
                        {artwork.ownerProfile?.socials.twitter && (
                          <a href={`https://twitter.com/${artwork.ownerProfile?.socials?.twitter}`} target="_blank" rel="noopener noreferrer">
                            <Twitter className="w-4 h-4 hover:text-blue-400" />
                          </a>
                        )}
                        {artwork.ownerProfile?.socials.instagram && (
                          <a href={`https://instagram.com/${artwork.ownerProfile?.socials?.instagram}`} target="_blank" rel="noopener noreferrer">
                            <Instagram className="w-4 h-4 hover:text-pink-400" />
                          </a>
                        )}
                        {artwork.ownerProfile?.socials.youtube && (
                          <a href={`https://youtube.com/${artwork.ownerProfile?.socials?.youtube}`} target="_blank" rel="noopener noreferrer">
                            <Youtube className="w-4 h-4 hover:text-red-700" />
                          </a>
                        )}
                        {artwork.ownerProfile?.socials.twitch && (
                          <a href={`https://twitch.tv/${artwork.ownerProfile?.socials?.twitch}`} target="_blank" rel="noopener noreferrer">
                            <Twitch className="w-4 h-4 hover:text-purple-500" />
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Info Section */}
                <div className="grid grid-cols-3 gap-4 mb-4 text-sm">
                  <div>
                    <div className="text-zinc-400 text-xs">EXPIRATION</div>
                    <div className={timeRemaining === '00:00:00' ? "text-white-400" : "font-semibold text-purple-400"}>
                      {timeRemaining}
                    </div>
                  </div>
                  <div>
                    <div className="text-zinc-400 text-xs">SIZE</div>
                    <div className="font-semibold">
                      {artwork?.size ? `${artwork.size} px` : '—'}
                    </div>
                  </div>
                  <div>
                    <div className="text-zinc-400 text-xs">CREATE DATE</div>
                    <div className="font-semibold">
                      {artwork?.createdAt
                        ? new Date(artwork.createdAt).toLocaleDateString('en-US', {
                            month: 'long',
                            day: 'numeric',
                            year: 'numeric',
                            timeZone: 'America/Phoenix', // optional, pick your TZ
                          })
                        : '—'}
                    </div>
                  </div>
                </div>

                {/* Description */}
                <div className="mb-4 mt-4">
                  <div className="text-zinc-400 text-xs mb-1 flex justify-between items-center">
                    <span>DESCRIPTION</span>
                  </div>

                  <div className="font-semibold">
                    {artwork?.description ? artwork.description : "N/A"}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Separator line */}
          <Divider className="my-4" />

          {/* Footer actions */}
          <div className="flex mt-4 gap-2">
            <Button
              onPress={onLikeArtwork}
              className="w-3/4 px-26 py-2 border-2 border-zinc-200 text-white rounded-2xl transition-colors bg-slate-800 hover:bg-pink-400"
            >
              Like Artwork
            </Button>
            <Button
              onPress={onReportArtwork}
              isDisabled={isCallingBackend}
              className="w-1/4 px-26 py-2 border-2 border-zinc-200 text-white rounded-2xl transition-colors bg-slate-800 hover:bg-indigo-700"
            >
              Report Artwork
            </Button>
          </div>
        </div>
      </div>

      <Modal
        isOpen={reportModalOpen}
        onClose={onCancelReport}
        backdrop="blur"
        classNames={{
          wrapper: 'z-[1200]',
          base: 'bg-slate-900 text-white',
          header: 'border-b border-zinc-800',
          footer: 'border-t border-zinc-800',
        }}
        hideCloseButton={isCallingBackend}  // prevent closing while calling
      >
        <ModalContent>
          <ModalHeader className="text-lg font-semibold">
            Report Artwork
          </ModalHeader>
          <ModalBody>
            <p className="text-sm text-zinc-300">
              Are you sure you want to report
              {' '}<span className="font-medium text-white">{artwork?.title ?? 'this artwork'}</span>?
            </p>
            <p className="text-xs text-zinc-400">
              Our team will review this submission. False reports may result in account action.
            </p>
          </ModalBody>
          <ModalFooter>
            <Button
              className="py-2 border-2 border-zinc-200 text-white rounded-2xl transition-colors bg-slate-800 hover:bg-indigo-700"
              onPress={onCancelReport}
              isDisabled={isCallingBackend}
            >
              Cancel
            </Button>
            <Button
              color="danger"
              onPress={onConfirmReport}
              isDisabled={isCallingBackend}
              className="font-semibold"
            >
              {isCallingBackend ? 'Reporting…' : 'Confirm'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <LogInRequiredModal
        isOpen={loginModalOpen}
        onClose={() => setLoginModalOpen(false)}
        message="You must be logged in to like Artworks."
      />
    </>
  );
};

export default ArtworkGuestModal;
